import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Train, ArrowRight, Loader2 } from "lucide-react";
import Honeypot from "../components/Honeypot";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/search";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — must stay empty
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (website) return; // client-side short-circuit; backend also rejects
    setError("");
    setLoading(true);
    try {
      await register(name, email, password);
      navigate(next);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-screen flex items-center justify-center px-6 pt-safe-navbar">
      <div className="iq-card w-full max-w-md p-8 animate-fade-up">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <span className="inline-flex h-9 w-9 items-center justify-center bg-gold text-ink rounded-sm">
            <Train className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <span className="font-display text-xl tracking-[0.18em] text-cream">INTERQUEST</span>
        </Link>
        <h1 className="font-display text-cream text-4xl tracking-wider mb-1">Join the journey</h1>
        <p className="font-serif italic text-cream-dim mb-6">A passport, free of charge.</p>

        <form onSubmit={onSubmit} className="space-y-4">
          <Honeypot value={website} onChange={setWebsite} name="website" />
          <div>
            <label className="iq-label">Name</label>
            <input
              data-testid="register-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="iq-input"
              placeholder="Conductor"
            />
          </div>
          <div>
            <label className="iq-label">Email</label>
            <input
              data-testid="register-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="iq-input"
            />
          </div>
          <div>
            <label className="iq-label">Password (min 6 chars)</label>
            <input
              data-testid="register-password"
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="iq-input"
            />
          </div>
          {error && (
            <div data-testid="register-error" className="border border-accent-red/40 bg-accent-red/10 text-cream px-3 py-2 rounded-sm text-sm">
              {error}
            </div>
          )}
          <button data-testid="register-submit" type="submit" disabled={loading} className="iq-btn-primary w-full">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Create account <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>

        <p className="text-center text-sm text-cream-dim mt-6">
          Already aboard?{" "}
          <Link to="/login" data-testid="link-login" className="text-gold hover:text-cream">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
